import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { WorkOrderCommentsService } from '@app/work-order/work-order-comments.service';
import { WorkOrderDetailsService } from 'src/app/work-order/work-order-details.service';
import { CommentDto, WorkOrderDetailsDto } from '@app/generated/model';
import { MatDialog } from '@angular/material/dialog';
import { AddCommentComponent } from './add-comment.component';
import { CommentContext, AddEditCommentModel } from './add-comment.model';
import { ActivatedRoute } from '@angular/router';
import { createFilters } from './filter-list-factory';
import { FilterOption } from './filter-option';
import { WebSocketService } from '@app/core/services/websocket.service';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { BadgeNotification } from '@app/core/model/badge-notification.model';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LoadWorkOrder } from '@dwo/actions';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-work-order-comments',
  templateUrl: './work-order-comments.component.html',
  styleUrls: ['./work-order-comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderCommentsComponent implements OnInit, OnDestroy {
  private comments: CommentDto[];
  private filters: FilterOption[];
  workOrder: WorkOrderDetailsDto;

  public commentIds: Record<string, BadgeNotification> = {};
  private commentNotifications: BadgeNotification[] = [];

  private notificationSubscription$: Subscription;
  private destroy$ = new Subject<void>();
  constructor(
    private workOrderDetailsService: WorkOrderDetailsService,
    private workOrderCommentsService: WorkOrderCommentsService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private store: Store,
    private offlineData: OfflineDataDb,
    private webSocketService: WebSocketService,
  ) {}

  async ngOnInit() {
    // if (!this.workOrderCommentsService.commentsLoaded()) {
    //   this.workOrderCommentsService.load();
    // }
    // if (!this.workOrderDetailsService.workOrderLoaded()) {
    //   this.workOrderDetailsService.load();
    // }
    // this.workOrder = this.workOrderDetailsService.get();

    this.workOrderCommentsService.comments$.pipe(takeUntil(this.destroy$)).subscribe((comments) => {
      this.comments = comments ?? [];
      this.filters = createFilters(this.comments.filter((c) => !c.parentId));
    });

    this.workOrderDetailsService.workOrder$.pipe(takeUntil(this.destroy$)).subscribe((workOrder) => {
      if (workOrder) {
        this.workOrder = workOrder;
      } else {
        const workOrderId = this.route.parent?.parent?.snapshot.paramMap.get('id');
        if (workOrderId) {
          this.store.dispatch(new LoadWorkOrder(workOrderId));
        }
      }
    });

    const referenceType = this.route.snapshot.paramMap.get('referenceType');
    const referenceId = this.route.snapshot.paramMap.get('referenceId');

    this.setSelectedFilter(referenceType, referenceId);

    this.notificationSubscription$ = this.webSocketService.badgeNotifications$.subscribe(
      (notifications: BadgeNotification[]) => {
        if (notifications == null) {
          return;
        }

        const workOrderNotification = notifications.filter(
          (notification: BadgeNotification) =>
            notification.name.endsWith('WorkOrderEntity') && notification.id === this.workOrder.id,
        )[0];

        if (workOrderNotification) {
          this.commentNotifications = workOrderNotification.children;

          // create a map to avoid doing filter
          this.commentIds = this.commentNotifications.reduce((map, notification: BadgeNotification) => {
            map[notification.id] = notification;
            return map;
          }, {});

          if (this.commentNotifications.length > 0) {
            // wait 5 seconds before we send notification back
            setTimeout(() => this.webSocketService.sendNotificationsViewed(this.commentNotifications), 5000);
          }
        } else {
          this.commentNotifications = [];
          this.commentIds = {};
        }
      },
    );

    this.workOrderCommentsService.commentsUpdated.subscribe(() => {
      this.filters = createFilters(this.comments.filter((c) => !c.parentId));
    });
  }

  ngOnDestroy() {
    this.notificationSubscription$.unsubscribe();
  }

  hasNotification(commentId): boolean {
    return !!this.commentIds[commentId];
  }

  getComments(): CommentDto[] {
    if (!this.filters || !this.comments) {
      return [];
    }
    const topLevel = this.comments.filter((c) => !c.parentId);
    if (this.filters.some((f) => f.selected)) {
      return topLevel.filter((c) =>
        this.filters
          .filter((f) => f.selected)
          .some((f) => f.referenceId === c.referenceId && f.referenceType === c.referenceType),
      );
    }
    return topLevel;
  }

  getFilters(): FilterOption[] {
    return this.filters;
  }

  toggleFilter(filter: FilterOption) {
    filter.selected = !filter.selected;
  }

  private setSelectedFilter(referenceType: string, referenceId: string) {
    if (!this.filters) {
      return;
    }
    this.filters.forEach((f) => (f.selected = f.referenceType === referenceType && f.referenceId === referenceId));
  }

  getReferenceTypeKey(item: CommentDto | FilterOption) {
    return `workOrderComments.referenceType.${item.referenceType}`;
  }

  getReferenceLabel(item: CommentDto | FilterOption) {
    if (!this.workOrder) {
      return [];
    }
    if (item.referenceType === 'WORK_ORDER') {
      return this.workOrder.workOrderNumber;
    }

    if (item.referenceName) {
      return item.referenceName;
    }

    if (item.referenceId) {
      return item.referenceId;
    }

    return '';
  }

  getReferenceLabelWithCount(filter: FilterOption) {
    return `${this.getReferenceLabel(filter)} (${filter.count})`;
  }

  showAddNote(): void {
    const context: CommentContext = {
      referenceType: 'WORK_ORDER',
      referenceId: this.workOrder.id,
    };
    const addData: AddEditCommentModel = {
      context,
      comment: null,
    };

    this.dialog.open(AddCommentComponent, {
      width: '90vw',
      maxWidth: '810px',
      position: { top: '10%', left: '10%' },
      panelClass: 'dwo-dialog',
      data: addData, // you can adjust the value as per your view
    });
  }
}
